// This file is part of InvenioRDM
// Copyright (C) 2022-2024 CERN.
//
// Invenio RDM is free software; you can redistribute it and/or modify it
// under the terms of the MIT License; see LICENSE file for more details.

import React, { Component } from "react";
import PropTypes from "prop-types";
import { i18next } from "@translations/invenio_app_rdm/i18next";
import { withCancel, http } from "react-invenio-forms";
import {
  Placeholder,
  Divider,
  Container,
  Header,
  Item,
  Button,
  Message,
  Grid,
} from "semantic-ui-react";
import Overridable from "react-overridable";
import RecordsResultsListItem from "@js/invenio_app_rdm/components/RecordsResultsListItem";
import isEmpty from "lodash/isEmpty";
import { buildUID } from "react-searchkit";

export class RecordsList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      data: { hits: [] },
      isLoading: false,
      error: null,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentWillUnmount() {
    this.cancellableFetch && this.cancellableFetch.cancel();
  }

  fetchData = async () => {
    const { fetchUrl } = this.props;
    console.log('=== fetchData debug ===');
    console.log('fetchUrl:', fetchUrl);
    this.setState({ isLoading: true });

    this.cancellableFetch = withCancel(
      http.get(fetchUrl, {
        headers: {
          Accept: "application/vnd.inveniordm.v1+json",
        },
      })
    );

    try {
      const response = await this.cancellableFetch.promise;
      console.log('=== fetchData success ===');
      console.log('response:', response);
      console.log('response.data:', response.data);
      console.log('response.data type:', typeof response.data);
      console.log('response.data.hits:', response.data.hits);
      console.log('response.data.hits type:', typeof response.data.hits);
      console.log('response.data.hits.hits:', response.data.hits?.hits);
      console.log('response.data.hits.hits length:', response.data.hits?.hits?.length || 0);
      console.log('About to setState with:', response.data.hits);
      this.setState({ data: response.data.hits, isLoading: false });
      console.log('setState completed');
    } catch (error) {
      console.log('=== fetchData error ===');
      console.error('fetch error:', error);
      console.log('error type:', typeof error);
      console.log('error.response:', error.response);
      console.log('error.message:', error.message);
      let errorMessage = "An error occurred while fetching data";
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      console.log('Setting error state with message:', errorMessage);
      this.setState({ error: errorMessage, isLoading: false });
    }
  };

  renderPlaceHolder = () => {
    const { title } = this.props;

    return (
      <Container>
        <Header as="h2">{title}</Header>
        {Array.from(Array(10)).map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index}>
            <Placeholder fluid className="rel-mt-3">
              <Placeholder.Header>
                <Placeholder.Line />
              </Placeholder.Header>

              <Placeholder.Paragraph>
                <Placeholder.Line />
              </Placeholder.Paragraph>

              <Placeholder.Paragraph>
                <Placeholder.Line />
                <Placeholder.Line />
                <Placeholder.Line />
              </Placeholder.Paragraph>
            </Placeholder>

            {index < 9 && <Divider className="rel-mt-2 rel-mb-2" />}
          </div>
        ))}
      </Container>
    );
  };

  render() {
    const { isLoading, data, error } = this.state;
    const { title, appName } = this.props;

    // Comprehensive debugging
    console.log('=== RecordsList render() debug ===');
    console.log('isLoading:', isLoading);
    console.log('error:', error);
    console.log('data:', data);
    console.log('data type:', typeof data);
    console.log('data.hits:', data.hits);
    console.log('data.hits type:', typeof data.hits);
    console.log('data.hits is array:', Array.isArray(data.hits));
    console.log('data.hits length:', data.hits ? data.hits.length : 'undefined');
    console.log('Grid component:', Grid);
    console.log('Grid type:', typeof Grid);
    console.log('isEmpty function:', isEmpty);
    console.log('title:', title);
    console.log('appName:', appName);

    const listItems = data.hits?.map((record) => {
      console.log('Mapping record:', record);
      console.log('Record ID:', record.id);
      return (
        <RecordsResultsListItem result={record} key={record.id} appName={appName} />
      );
    }) || [];

    console.log('listItems:', listItems);
    console.log('listItems length:', listItems.length);
    console.log('isEmpty(listItems):', isEmpty(listItems));
    console.log('Render conditions:');
    console.log('  - !isLoading:', !isLoading);
    console.log('  - !error:', !error);
    console.log('  - !isEmpty(listItems):', !isEmpty(listItems));
    console.log('  - Combined condition:', !isLoading && !error && !isEmpty(listItems));
    console.log('=== End debug ===');

    const midpoint = Math.ceil(listItems.length / 2);
    const leftItems = listItems.slice(0, midpoint);
    const rightItems = listItems.slice(midpoint);
    const rows = [];

    for (let i = 0; i < Math.max(leftItems.length, rightItems.length); i++) {
      rows.push({
        left: leftItems[i],
        right: rightItems[i],
      });
    }

    return (
      <>
        {isLoading && (
          console.log('Rendering loading placeholder') ||
          this.renderPlaceHolder()
        )}

        {!isLoading && !error && !isEmpty(listItems) && (
          console.log('Rendering records list with', listItems.length, 'items') ||
          <Container>
            <Header as="h2">{title}</Header>

            {/* Two-column Grid layout */}
            <Grid columns={2} stackable className="frontpage-records">
              {rows.map((row, idx) => (
                <Grid.Row key={idx}>
                  <Grid.Column>
                    {row.left && (
                      <Item.Group relaxed link divided>
                        {row.left}
                      </Item.Group>
                    )}
                  </Grid.Column>
                  <Grid.Column>
                    {row.right && (
                      <Item.Group relaxed link divided>
                        {row.right}
                      </Item.Group>
                    )}
                  </Grid.Column>
                </Grid.Row>
              ))}
            </Grid>

            <Container textAlign="center">
              <Button href="/search">{i18next.t("More")}</Button>
            </Container>
          </Container>
        )}

        {!isLoading && !error && isEmpty(listItems) && (
          console.log('Rendering empty state') ||
          <Container>
            <Header as="h2">{title}</Header>
            <Message info>
              <Message.Header>{i18next.t("No records found")}</Message.Header>
              <p>{i18next.t("There are no records to display yet.")}</p>
            </Message>
            <Container textAlign="center">
              <Button href="/search">{i18next.t("Browse all records")}</Button>
            </Container>
          </Container>
        )}

        {error && (
          console.log('Rendering error state:', error) ||
          <Container>
            <Header as="h2">{title}</Header>
            <Message content={error} error icon="warning sign" />
          </Container>
        )}
      </>
    );
  }
}

RecordsList.propTypes = {
  title: PropTypes.string.isRequired,
  fetchUrl: PropTypes.string.isRequired,
  appName: PropTypes.string,
};

RecordsList.defaultProps = {
  appName: "",
};

export class RecordsListOverridable extends Component {
  render() {
    const { title, fetchUrl, appName } = this.props;
    return (
      <Overridable
        id={buildUID("layout", "", appName)}
        title={title}
        fetchUrl={fetchUrl}
        appName={appName}
      >
        <RecordsList title={title} fetchUrl={fetchUrl} appName={appName} />
      </Overridable>
    );
  }
}

RecordsListOverridable.propTypes = {
  title: PropTypes.string.isRequired,
  fetchUrl: PropTypes.string.isRequired,
  appName: PropTypes.string,
};

RecordsListOverridable.defaultProps = {
  appName: "",
};
